import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CircleCheck, Pencil, X } from 'lucide-react';
import { observer } from 'mobx-react';
import { FormSection } from '@components/form-section';
import { Spinner } from '@components/spinner';
import { Tooltip } from '@components/tooltip';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { useCommunitySettingsStore } from './store';
import InstalledAppsSection from './installed-apps-section';
import { links } from '@core/router';
import {
    AggregationAlgorithm,
    CommunityAccessLevel
} from '@core/api/ChallengeClient';

const Field = ({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <>
        <dt className="text-muted-foreground">{label}</dt>
        <dd>{children}</dd>
    </>
);

const ToggleBadge = ({
    enabled,
    labels = ['Enabled', 'Disabled']
}: {
    enabled?: boolean;
    labels?: [string, string];
}) => (
    <Badge variant={enabled ? 'success' : 'muted'}>
        {enabled ? <CircleCheck /> : <X />}
        {enabled ? labels[0] : labels[1]}
    </Badge>
);

const validityLabel = (validFrom?: string, validTo?: string) =>
    validFrom || validTo
        ? `Valid: ${validFrom || '...'} - ${validTo || '...'}`
        : '';

const FilterChip = ({
    label,
    validityText,
    variant
}: {
    label: string;
    validityText: string;
    variant: 'outline' | 'info';
}) => (
    <div className="flex flex-col gap-1">
        {validityText ? (
            <Tooltip title={validityText} hideIcon>
                <Badge variant={variant}>{label}</Badge>
            </Tooltip>
        ) : (
            <Badge variant={variant}>{label}</Badge>
        )}
        {validityText && (
            <div className="pl-6">
                <Badge variant="secondary">{validityText}</Badge>
            </div>
        )}
    </div>
);

const CommunitySettingsSection = observer(() => {
    const { communityId } = useParams();
    const navigate = useNavigate();
    const { communitySettings, leaderboardSettings } =
        useCommunitySettingsStore();

    React.useEffect(() => {
        if (communityId) {
            communitySettings.request([parseInt(communityId)]);
            leaderboardSettings.request([parseInt(communityId)]);
        }
    }, [communityId, communitySettings, leaderboardSettings]);

    return (
        <div>
            {communitySettings.match({
                default: () => <div></div>,
                loading: () => (
                    <div className="flex min-h-[300px] items-center justify-center">
                        <Spinner />
                    </div>
                ),
                error: () => (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Error loading settings:{' '}
                            {communitySettings.error!.message}
                        </AlertDescription>
                    </Alert>
                ),
                fetched: () => {
                    const settings = communitySettings.result!;
                    return (
                        <div className="grid gap-6 md:grid-cols-2">
                            <FormSection
                                title="General Settings"
                                actions={
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            navigate(
                                                links.editCommunitySettings(
                                                    communityId!
                                                )
                                            )
                                        }
                                    >
                                        <Pencil />
                                        Edit
                                    </Button>
                                }
                            >
                                <dl className="grid grid-cols-[200px_1fr] gap-y-2 text-sm">
                                    <Field label="Event Creation">
                                        {settings.eventCreation}
                                    </Field>
                                    <Field label="Member Invitation">
                                        {settings.memberInvitation}
                                    </Field>
                                    <Field label="Chat Notifications">
                                        {settings.notificationsChat}
                                    </Field>
                                    <Field label="Team Creation">
                                        {settings.teamCreation}
                                    </Field>
                                    <Field label="Feed Post Creation">
                                        {settings.feedPostCreation ??
                                            CommunityAccessLevel.ALL}
                                    </Field>
                                    <Field label="Default Team Size Limit">
                                        {settings.defaultTeamMemberLimit ?? (
                                            <em className="text-muted-foreground">
                                                No limit
                                            </em>
                                        )}
                                    </Field>
                                </dl>
                                <Separator className="my-4" />
                                <dl className="grid grid-cols-[200px_1fr] gap-y-2 text-sm">
                                    <Field label="Leaving">
                                        <ToggleBadge
                                            enabled={settings.leaveEnabled}
                                        />
                                    </Field>
                                    <Field label="Achievements">
                                        <ToggleBadge
                                            enabled={
                                                settings.achievementsEnabled
                                            }
                                        />
                                    </Field>
                                    <Field label="Duels">
                                        <ToggleBadge
                                            enabled={settings.duelsEnabled}
                                        />
                                    </Field>
                                    <Field label="Teams">
                                        <ToggleBadge
                                            enabled={settings.teamsEnabled}
                                        />
                                    </Field>
                                    <Field label="Single Team Membership">
                                        <ToggleBadge
                                            enabled={
                                                settings.singleTeamMembershipEnforced
                                            }
                                            labels={[
                                                'Enforced',
                                                'Not enforced'
                                            ]}
                                        />
                                    </Field>
                                    <Field label="Challenge Subscription Details">
                                        <ToggleBadge
                                            enabled={
                                                settings.challengeSubscriptionDetailsVisible
                                            }
                                            labels={['Visible', 'Hidden']}
                                        />
                                    </Field>
                                    <Field label="Live Events">
                                        <ToggleBadge
                                            enabled={settings.liveEventsEnabled}
                                        />
                                    </Field>
                                </dl>
                            </FormSection>

                            {leaderboardSettings.match({
                                default: () => <div></div>,
                                loading: () => (
                                    <div className="flex min-h-[200px] items-center justify-center">
                                        <Spinner />
                                    </div>
                                ),
                                error: () => (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            Error loading leaderboard settings:{' '}
                                            {leaderboardSettings.error!.message}
                                        </AlertDescription>
                                    </Alert>
                                ),
                                fetched: () => {
                                    const leaderboard =
                                        leaderboardSettings.result!;
                                    return (
                                        <FormSection
                                            title="Leaderboard Settings"
                                            actions={
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() =>
                                                        navigate(
                                                            links.editCommunityLeaderboardSettings(
                                                                communityId!
                                                            )
                                                        )
                                                    }
                                                >
                                                    <Pencil />
                                                    Edit
                                                </Button>
                                            }
                                        >
                                            <div className="flex flex-col gap-5 text-sm">
                                                <p>
                                                    <strong>
                                                        Points Aggregation:
                                                    </strong>{' '}
                                                    {leaderboard.pointsAggregationType ===
                                                    AggregationAlgorithm.SUM
                                                        ? 'Sum'
                                                        : 'Average'}
                                                </p>

                                                <div>
                                                    <p className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                                                        Date Filters
                                                    </p>
                                                    {leaderboard.dateFilters &&
                                                    leaderboard.dateFilters
                                                        .length > 0 ? (
                                                        <div className="flex flex-col gap-1">
                                                            {leaderboard.dateFilters.map(
                                                                (
                                                                    filter,
                                                                    index
                                                                ) => {
                                                                    const mainParts =
                                                                        [
                                                                            filter.filterType.toString()
                                                                        ];
                                                                    if (
                                                                        filter.dateFrom
                                                                    ) {
                                                                        mainParts.push(
                                                                            `${filter.dateFrom} - ${filter.dateTo || 'ongoing'}`
                                                                        );
                                                                    }
                                                                    return (
                                                                        <FilterChip
                                                                            key={
                                                                                index
                                                                            }
                                                                            label={mainParts.join(
                                                                                ' | '
                                                                            )}
                                                                            validityText={validityLabel(
                                                                                filter.validFrom,
                                                                                filter.validTo
                                                                            )}
                                                                            variant="outline"
                                                                        />
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-muted-foreground italic">
                                                            No date filters
                                                            configured
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <p className="mb-2 text-xs font-medium tracking-wide text-muted-foreground uppercase">
                                                        Activity Category
                                                        Filters
                                                    </p>
                                                    {leaderboard.activityCategoryFilters &&
                                                    leaderboard
                                                        .activityCategoryFilters
                                                        .length > 0 ? (
                                                        <div className="flex flex-col gap-1">
                                                            {leaderboard.activityCategoryFilters.map(
                                                                (
                                                                    filter,
                                                                    index
                                                                ) => {
                                                                    const mainParts =
                                                                        [
                                                                            filter.metric.toString()
                                                                        ];
                                                                    if (
                                                                        filter.activityType
                                                                    )
                                                                        mainParts.push(
                                                                            filter.activityType.toString()
                                                                        );
                                                                    if (
                                                                        filter.workoutCategory
                                                                    )
                                                                        mainParts.push(
                                                                            filter.workoutCategory.toString()
                                                                        );
                                                                    if (
                                                                        filter.workoutType
                                                                    )
                                                                        mainParts.push(
                                                                            filter.workoutType.toString()
                                                                        );
                                                                    if (
                                                                        filter.rewardCurrencyCode
                                                                    )
                                                                        mainParts.push(
                                                                            `Currency: ${filter.rewardCurrencyCode}`
                                                                        );
                                                                    if (
                                                                        filter.pointTypeKey
                                                                    )
                                                                        mainParts.push(
                                                                            `Key: ${filter.pointTypeKey}`
                                                                        );
                                                                    if (
                                                                        filter.challengeId
                                                                    )
                                                                        mainParts.push(
                                                                            `Challenge: ${filter.challengeId}`
                                                                        );
                                                                    if (
                                                                        filter.challengeLeaderboardType
                                                                    )
                                                                        mainParts.push(
                                                                            filter.challengeLeaderboardType.toString()
                                                                        );
                                                                    if (
                                                                        filter.genderFilter &&
                                                                        filter.genderFilter !==
                                                                            'DISABLED'
                                                                    )
                                                                        mainParts.push(
                                                                            `Gender: ${filter.genderFilter}`
                                                                        );
                                                                    return (
                                                                        <FilterChip
                                                                            key={
                                                                                index
                                                                            }
                                                                            label={mainParts.join(
                                                                                ' - '
                                                                            )}
                                                                            validityText={validityLabel(
                                                                                filter.validFrom,
                                                                                filter.validTo
                                                                            )}
                                                                            variant="info"
                                                                        />
                                                                    );
                                                                }
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <p className="text-muted-foreground italic">
                                                            No activity category
                                                            filters configured
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </FormSection>
                                    );
                                }
                            })}

                            <InstalledAppsSection apps={settings.apps} />
                        </div>
                    );
                }
            })}
        </div>
    );
});

export default CommunitySettingsSection;
